import React, { useImperativeHandle, useRef, useState } from "react";
import "react-image-gallery/styles/css/image-gallery.css";
import "./ImageSlider.css";
import addImagesIcon from "../../assets/icons/add-images-icon.png";
import removeImageIcon from "../../assets/icons/remove-image-icon.png";
import Icon from "../Icon/Icon";
import Hover from "../Hover/Hover";
import { useSnackbar } from "notistack";
import { backendUrl } from "../../api/constants";
import { useEffect } from "react";
import ImageSliderComponent from "./ImageSliderComponent";
import { HiOutlineCamera } from "react-icons/hi2";
import { useEnv } from "../../context/envContext/envContext";
import SortableImageStrip from "./SortableImageStrip";
import { cropImageModal } from "../../util/imageCroper";

const getJpegFileName = (fileName = "image") => {
  const baseName = fileName.replace(/\.[^/.]+$/, "") || "image";
  return `${baseName}.jpg`;
};

const toDisplayItems = (images = []) =>
  images
    .map((img) => {
      if (!img) return null;
      if (typeof img !== "string") return img;
      if (img.includes("http")) return { original: img };
      return { original: `${backendUrl}/assets/${img}` };
    })
    .filter(Boolean);

const urlsToGalleryItems = async (images) => {
  const imgs = [...images];

  // Asynchronously check each image URL
  const checkedImages = await Promise.all(
    imgs.map(async (img) => {
      // Directly return non-string images or local images
      if (typeof img !== "string") {
        return img;
      }

      // External URLs (already absolute) — trust them directly, skip HEAD check
      // S3 and other CDN URLs often block cross-origin HEAD requests via CORS
      // but serve fine as <img src>
      if (img.includes("http")) {
        return { original: img };
      }

      // Local asset paths — prepend backend URL and validate with HEAD
      const imageUrl = `${backendUrl}/assets/${img}`;
      try {
        const response = await fetch(imageUrl, { method: "HEAD" });
        if (response.status === 200) {
          return { original: imageUrl };
        } else {
          return null;
        }
      } catch (error) {
        return null;
      }
    })
  );

  return checkedImages.filter(Boolean);
};

const ImageSlider = React.forwardRef(
  (
    { images = [], onChange, style, className, edit = false, showThumbnails, emptyHint },
    ref
  ) => {
    const [hovered, setHovered] = useState(false);
    const [selectedImages, setSelectedImages] = useState(() => toDisplayItems(images));
    const [currentIndex, setCurrentIndex] = useState(0);
    const sliderRef = useRef();
    const { enqueueSnackbar } = useSnackbar();

    const { showThumbnails: st } = useEnv();
    const previousSelectedLengthRef = useRef(0);

    //if showThumbnails is undefined, use the showThumbnails from the env context
    showThumbnails = showThumbnails === undefined ? st : showThumbnails;

    useEffect(() => {
      if (sliderRef.current) {
        sliderRef.current.slideToIndex(currentIndex);
      }
    }, [currentIndex]);

    useEffect(() => {
      if (onChange) {
        onChange(selectedImages);
      }

      if (
        edit &&
        sliderRef.current &&
        selectedImages.length > previousSelectedLengthRef.current
      ) {
        const lastIndex = selectedImages.length - 1;
        setCurrentIndex(lastIndex);
        sliderRef.current.slideToIndex(lastIndex);
      }
      previousSelectedLengthRef.current = selectedImages.length;
    }, [selectedImages]);

    const previousImagesRef = useRef();
    const validatedImagesRef = useRef(new Set()); // Track which image sets have been validated
    
    useEffect(() => {
      const imagesKey = JSON.stringify(images);
      const hasChanged = imagesKey !== JSON.stringify(previousImagesRef.current);
      const alreadyValidated = validatedImagesRef.current.has(imagesKey);

      if (!hasChanged && alreadyValidated) return;

      if (hasChanged) {
        setSelectedImages(toDisplayItems(images));
        previousImagesRef.current = images;
      }

      const needsValidation = (images || []).some(
        (img) => typeof img === "string" && !img.includes("http")
      );

      if (!needsValidation) {
        validatedImagesRef.current.add(imagesKey);
        return;
      }

      let cancelled = false;
      urlsToGalleryItems(images).then((validatedImages) => {
        if (cancelled) return;
        setSelectedImages(validatedImages);
        validatedImagesRef.current.add(imagesKey);
      });

      return () => {
        cancelled = true;
      };
    }, [images]);

    useImperativeHandle(ref, () => ({
      // Expose functions or values to the parent component through the ref
      getValue: () => {
        return selectedImages?.filter((img) => img.file).map((img) => img.file);
      },
      getURLs: () => {
        return selectedImages
          ?.filter((img) => img.original && !img.file)
          .map((img) => {
            //take only /assets/... part of the url
            return img.original.split("/assets/")[1];
          });
      },
      setValue: async (newValue) => {
        setSelectedImages(await urlsToGalleryItems(newValue));
      },
    }));

    const pickImages = async (add = false, maxImages = 10) => {
      try {
        const fileInput = document.createElement("input");
        fileInput.style.display = "none";
        fileInput.type = "file";
        fileInput.multiple = true;
        fileInput.accept = "image/*";

        document.body.appendChild(fileInput);

        // Listen for the file input change event
        fileInput.addEventListener("change", async (event) => {
          const files = Array.from(event.target.files);
          document.body.removeChild(fileInput); // Remove the input element immediately after use

          if (files.length === 0) {
            return;
          }

          if (files.length > maxImages) {
            enqueueSnackbar(`Maximum ${maxImages} images allowed.`, {
              variant: "error",
            });
            return;
          }

          const processFiles = async (files) => {
            const processedImages = [];
            for (const file of files) {
              const reader = new FileReader();
              const base64 = await new Promise((resolve) => {
                reader.onload = () => resolve(reader.result);
                reader.readAsDataURL(file);
              });

              // Call the cropImageModal to let the user crop the image
              const croppedImage = await cropImageModal({
                image: { file, base64 },
                borderRadius: 0,
              });
              if (croppedImage) {
                // Check if the cropping was not cancelled
                const blob = croppedImage.blob || await fetch(croppedImage.base64).then((res) =>
                  res.blob()
                );
                const croppedFile = new File([blob], getJpegFileName(file.name), {
                  type: croppedImage.mimeType || blob.type || "image/jpeg",
                });

                processedImages.push({
                  file: croppedFile,
                  original: URL.createObjectURL(croppedFile),
                });
              }
            }
            return processedImages;
          };

          const selectedImageURLs = await processFiles(files);

          // Update the state based on whether images are added or replaced
          if (add) {
            setSelectedImages((prev) => [...prev, ...selectedImageURLs]);
          } else {
            setSelectedImages(selectedImageURLs);
          }
        });

        // Trigger the file input dialog
        fileInput.click();
      } catch (error) {
        enqueueSnackbar("error picking images", { variant: "error" });
      }
    };

    const removeImage = () => {
      const removeIndex = sliderRef.current?.getCurrentIndex() ?? currentIndex;
      setSelectedImages((prev) => {
        const nextImages = prev.filter((im, i) => i !== removeIndex);
        const nextIndex = Math.max(0, Math.min(removeIndex, nextImages.length - 1));
        setCurrentIndex(nextIndex);
        sliderRef.current?.slideToIndex(nextIndex);
        return nextImages;
      });
    };

    // Add this function to determine the appropriate className based on whether images exist
    const getContainerClassName = () => {
      const hasImages = selectedImages && selectedImages.length > 0;
      if (typeof className === "function") {
        return className(hasImages);
      }
      return className || "";
    };

    const renderThumbnails = () => {
      if (!selectedImages || selectedImages.length <= 1) return null;

      return (
        <SortableImageStrip
          items={selectedImages}
          getImageSrc={(image) => image.original}
          selectedIndex={currentIndex}
          onSelect={(index) => {
            setCurrentIndex(index);
            sliderRef.current?.slideToIndex(index);
          }}
          onReorder={
            edit
              ? (nextImages, nextIndex) => {
                setSelectedImages(nextImages);
                setCurrentIndex(nextIndex);
                sliderRef.current?.slideToIndex(nextIndex);
              }
              : undefined
          }
        />
      );
    };

    return (
      <>
        {((selectedImages && selectedImages.length > 0) || edit) && (
          <div
            className={`relative max-w-[100%] ${getContainerClassName()}`}
            onClick={(e) => {
              e.stopPropagation();
            }}
            onMouseEnter={() => {
              setHovered(true);
            }}
            onMouseLeave={() => {
              setHovered(false);
            }}
          >
            {edit && selectedImages && selectedImages.length > 0 && (
              <>
                <Hover
                  hstyle={styles.hoveredIcon}
                  style={{
                    ...styles.controlIcon,
                    ...styles.addIcon,
                    ...(hovered ? styles.hoveredIconStyle : {}),
                  }}
                  onClick={() => {
                    pickImages(true);
                  }}
                >
                  <Icon src={addImagesIcon} />
                </Hover>
                <Hover
                  hstyle={styles.hoveredIcon}
                  style={{
                    ...styles.controlIcon,
                    ...styles.removeIcon,
                    ...(hovered ? styles.hoveredIconStyle : {}),
                  }}
                  onClick={removeImage}
                >
                  <Icon src={removeImageIcon} />
                </Hover>
              </>
            )}
            {selectedImages && selectedImages.length > 0 ? (
              <>
                <div
                  className="image-slider-selected-content"
                  style={{ userSelect: "none" }}
                  onDragStart={(e) => e.preventDefault()}
                >
                  <ImageSliderComponent
                    ref={sliderRef}
                    items={selectedImages}
                    onSlideChange={setCurrentIndex}
                  />
                </div>

                {/* Add the thumbnails section */}
              </>
            ) : (
              <>
                {edit ? (
                  <div
                    onClick={pickImages}
                    className="inyc-dropzone"
                  >
                    <div
                      className="flex items-center justify-center"
                      style={{
                        ...styles.noImagesImage,
                        ...(hovered ? styles.noImagesImageHover : {}),
                      }}
                    >
                      <div className="flex flex-col items-center text-center">
                        <HiOutlineCamera
                          size={40}
                          className="mb-kit-8"
                          color="var(--inyc-no-image-color)"
                        />
                        <span className="text-[var(--inyc-no-image-color)] font-['Primary-Regular'] text-kit-md">
                          click to upload
                        </span>
                        {emptyHint && (
                          <span className="mt-kit-6 text-[var(--inyc-no-image-color)] font-['Primary-Medium'] text-kit-sm opacity-70">
                            {emptyHint}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="inyc-dropzone pointer-events-none"></div>
                )}
              </>
            )}
          </div>
        )}
        {(edit || showThumbnails) && renderThumbnails()}
      </>
    );
  }
);

const styles = {
  imageSlider: {
    // boxShadow: "var(--inyc-shadow-card)",
    maxWidth: "100%",
    height: "100%",
    // margin: "var(--inyc-space-20)",
    position: "relative",
  },
  controlIcon: {
    transition: "0.3s",
    opacity: 0,
    backgroundColor: "black",
    padding: "var(--inyc-space-8)",
    position: "absolute",
    zIndex: 1,
    height: "40px",
    width: "40px",
    color: "white",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    borderRadius: "50%",
    cursor: "pointer",
    boxShadow: "var(--inyc-shadow-sm)",
  },
  noImagesImage: {
    width: 140,
    height: 100,
    transition: "0.3s",
  },
  noImagesImageHover: {
    transform: "scale(1.1)",
  },
  addIcon: {
    top: 0,
    right: 0,
    transform: "translate(50%,-50%)",
  },
  removeIcon: {
    top: 0,
    left: 0,
    transform: "translate(-50%,-50%)",
  },
  hoveredIconStyle: {
    opacity: 1,
  },
  hoveredIcon: {
    backgroundColor: "rgb(102, 102, 102)",
  },
};

export default ImageSlider;
